import {
  ApolloCache,
  gql,
  useMutation,
  useQuery,
} from "@apollo/client";
import { useMemo } from "react";
import {
  USER_MODEL_3D_FIELDS,
} from "./userModel3D";
import { UserModel3DLibraryItem } from "../features/floorplan3d/userModels/userModel3DTypes";

export type UserFavoriteUserModelsQuery = {
  userFavoriteUserModels: UserModel3DLibraryItem[];
};

export const UserFavoriteUserModelsDocument = gql`
  ${USER_MODEL_3D_FIELDS}
  query FloorplannerUserFavoriteUserModels {
    userFavoriteUserModels {
      ...UserModel3DFields
    }
  }
`;

export const AddUserModelToFavoritesDocument = gql`
  mutation FloorplannerAddUserModelToFavorites($id: ID!) {
    addUserModelToFavorites(id: $id)
  }
`;

export const RemoveUserModelFromFavoritesDocument = gql`
  mutation FloorplannerRemoveUserModelFromFavorites($id: ID!) {
    removeUserModelFromFavorites(id: $id)
  }
`;

const invalidateFavoriteUserModelsCache = (cache: ApolloCache<unknown>) => {
  cache.evict({
    id: "ROOT_QUERY",
    fieldName: "userFavoriteUserModels",
  });
  cache.gc();
};

/** Account-scoped community / user 3D model favorites. */
export function useUserFavoriteUserModels(skip = false) {
  return useQuery<UserFavoriteUserModelsQuery>(UserFavoriteUserModelsDocument, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    skip,
  });
}

/**
 * Add / remove a community 3D model from account favorites with optimistic cache updates.
 */
export function useToggleUserModelFavorite() {
  const [addFavorite, addState] = useMutation(AddUserModelToFavoritesDocument);
  const [removeFavorite, removeState] = useMutation(
    RemoveUserModelFromFavoritesDocument,
  );

  const toggle = async (
    model: UserModel3DLibraryItem,
    currentlyFavorite: boolean,
  ) => {
    if (currentlyFavorite) {
      return removeFavorite({
        variables: { id: model.id },
        optimisticResponse: {
          __typename: "Mutation",
          removeUserModelFromFavorites: model.id,
        },
        update: (cache) => {
          const existing = cache.readQuery<UserFavoriteUserModelsQuery>({
            query: UserFavoriteUserModelsDocument,
          });
          if (!existing) return;
          const next = existing.userFavoriteUserModels.filter(
            (item) => item.id !== model.id,
          );
          invalidateFavoriteUserModelsCache(cache);
          cache.writeQuery({
            query: UserFavoriteUserModelsDocument,
            data: { userFavoriteUserModels: next },
          });
        },
      });
    }

    return addFavorite({
      variables: { id: model.id },
      optimisticResponse: {
        __typename: "Mutation",
        addUserModelToFavorites: model.id,
      },
      update: (cache) => {
        const existing = cache.readQuery<UserFavoriteUserModelsQuery>({
          query: UserFavoriteUserModelsDocument,
        });
        if (!existing) {
          invalidateFavoriteUserModelsCache(cache);
          cache.writeQuery({
            query: UserFavoriteUserModelsDocument,
            data: { userFavoriteUserModels: [model] },
          });
          return;
        }
        if (existing.userFavoriteUserModels.some((item) => item.id === model.id)) {
          return;
        }
        invalidateFavoriteUserModelsCache(cache);
        cache.writeQuery({
          query: UserFavoriteUserModelsDocument,
          data: {
            userFavoriteUserModels: [model, ...existing.userFavoriteUserModels],
          },
        });
      },
    });
  };

  return {
    toggle,
    loading: addState.loading || removeState.loading,
  };
}

export function useIsUserModelFavorite(modelId: string): boolean {
  const { data } = useUserFavoriteUserModels();
  return useMemo(
    () =>
      Boolean(data?.userFavoriteUserModels?.some((model) => model.id === modelId)),
    [data?.userFavoriteUserModels, modelId],
  );
}
